/**
 * LYT Communications - Payment Invoice Page (Track 2)
 * Version: 1.0.0
 * Created: 2026-03-04
 * Route: #payment-invoices
 *
 * Traditional invoicing for splicing, special builds, and maintenance.
 * Creates invoices from QC-approved devices, emails to client,
 * tracks approval and payment.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Plus, Send, FileText, DollarSign, Loader, X,
  ChevronDown, ChevronUp, Check, CreditCard, Trash2, Printer,
  Sun, Moon
} from 'lucide-react';
import {
  loadFullProject, loadPaymentInvoices, createPaymentInvoice,
  updatePaymentInvoiceField, markDevicesBilled, getNextInvoiceNumber,
  fetchFullRates
} from '../services/mapService';
import { sendEmailNotification } from '../services/notificationService';
import { getSpliceCodeSet } from '../config/mapConfig';
import { getClientConfig } from '../config/constants';

function PaymentInvoicePage({ darkMode, user, setCurrentPage, projectId }) {
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [view, setView] = useState('list'); // list | create
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Create form state
  const [invoiceType, setInvoiceType] = useState('splicing');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [customItems, setCustomItems] = useState([]);
  const [clientEmail, setClientEmail] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');

  // Payment/approval modals
  const [approvalModal, setApprovalModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const border = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const muted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const [data, invs] = await Promise.all([
        loadFullProject(projectId),
        loadPaymentInvoices(projectId),
      ]);
      setDevices(data.devices || []);
      setProject(data.project || null);
      setInvoices(invs || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const clientConfig = getClientConfig(project?.client);
  const clientName = clientConfig.companyName || project?.client || 'Client';
  const rateCardId = clientConfig?.rateCard || 'vexus-la-tx-2026';
  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [fullRateInfo, setFullRateInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetchFullRates(rateCardId)
      .then(d => { if (live && d) setFullRateInfo(d); })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, [rateCardId]);
  const rates = fullRateInfo?.rates || {};
  const priceField = fullRateInfo?.priceField || 'client_rate';
  const spliceCodeSet = getSpliceCodeSet(rateCardId);

  // Billable devices: QC approved and not yet billed
  const billableDevices = useMemo(() => {
    return devices.filter(d =>
      d.status === 'QC Approved' &&
      (!d.billing_status || d.billing_status === 'Not Billed') &&
      (invoiceType === 'splicing'
        ? ['splice_point', 'splitter_1x4', 'splitter_2x8'].includes(d.device_type)
        : true)
    );
  }, [devices, invoiceType]);

  // Generate line items from selected devices
  const deviceLineItems = useMemo(() => {
    const items = [];
    billableDevices.filter(d => selectedDevices.has(d.device_id)).forEach(dev => {
      // Bastrop path: use vendor_codes from Vitruvi if available
      if (dev.vendor_codes && Array.isArray(dev.vendor_codes) && dev.vendor_codes.length > 0) {
        for (const vc of dev.vendor_codes) {
          if (!vc.code) continue;
          const rate = rates?.[vc.code]?.[priceField] || 0;
          const qty = parseFloat(vc.actual_qty) || parseInt(dev.qty) || 1;
          items.push({
            device_id: dev.device_id,
            code: vc.code,
            description: `${vc.description || vc.code} — ${dev.location || dev.device_id}`,
            qty,
            uom: vc.uom || 'EA',
            rate,
            total: Math.round(qty * rate * 100) / 100,
          });
        }
        return;
      }

      // Vexus/fallback path: derive codes from splice code set
      const unitCode = dev.unit_code || spliceCodeSet.spliceCode;
      const rate = rates?.[unitCode]?.[priceField] || 0;
      const qty = parseInt(dev.qty) || 1;

      if (['splice_point', 'splitter_1x4', 'splitter_2x8'].includes(dev.device_type)) {
        // Splice billing
        const fiberCount = parseInt(dev.fiber_count) || 2;
        const spliceType = dev.splice_type || '1x4';

        // Ring cut or case setup
        if (dev.position_type === 'mid-span') {
          const rcCode = spliceCodeSet.ringCutCode;
          const rcRate = rates?.[rcCode]?.[priceField] || 0;
          items.push({ device_id: dev.device_id, code: rcCode, description: `Ring cut — ${dev.location || dev.device_id}`, qty: 1, uom: 'EA', rate: rcRate, total: rcRate });
        } else {
          const clCode = spliceCodeSet.closureCode;
          const clRate = rates?.[clCode]?.[priceField] || 0;
          items.push({ device_id: dev.device_id, code: clCode, description: `Case setup — ${dev.location || dev.device_id}`, qty: 1, uom: 'EA', rate: clRate, total: clRate });
        }

        // Fusion splices
        const spCode = spliceCodeSet.spliceCode;
        const spRate = rates?.[spCode]?.[priceField] || 0;
        items.push({ device_id: dev.device_id, code: spCode, description: `Fusion splice (${fiberCount} fibers) — ${dev.location || dev.device_id}`, qty: fiberCount, uom: 'EA', rate: spRate, total: Math.round(fiberCount * spRate * 100) / 100 });

        // PM tests for 1x4
        if (spliceType === '1x4') {
          const tCode = spliceCodeSet.testCode;
          const tRate = rates?.[tCode]?.[priceField] || 0;
          const testCount = 8;
          items.push({ device_id: dev.device_id, code: tCode, description: `PM test (${testCount} readings) — ${dev.location || dev.device_id}`, qty: testCount, uom: 'EA', rate: tRate, total: Math.round(testCount * tRate * 100) / 100 });
        }
      } else {
        // Generic device billing
        items.push({
          device_id: dev.device_id, code: unitCode,
          description: `${dev.device_type} ${dev.size || ''} — ${dev.location || dev.device_id}`.trim(),
          qty, uom: 'EA', rate, total: Math.round(qty * rate * 100) / 100,
        });
      }
    });
    return items;
  }, [billableDevices, selectedDevices, rates, priceField, spliceCodeSet]);

  // All line items = device items + custom items
  const allLineItems = useMemo(() => [...deviceLineItems, ...customItems], [deviceLineItems, customItems]);
  const subtotal = useMemo(() => allLineItems.reduce((sum, li) => sum + (li.total || 0), 0), [allLineItems]);
  const retainage = Math.round(subtotal * 0.10 * 100) / 100;
  const netAmount = Math.round((subtotal - retainage) * 100) / 100;

  const handleToggleDevice = (deviceId) => {
    setSelectedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedDevices.size === billableDevices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(billableDevices.map(d => d.device_id)));
    }
  };

  const handleAddCustomItem = () => {
    setCustomItems(prev => [...prev, { _custom: true, code: '', description: '', qty: 1, uom: 'EA', rate: 0, total: 0 }]);
  };

  const handleUpdateCustomItem = (index, field, value) => {
    setCustomItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'qty' || field === 'rate') {
        const qty = parseFloat(field === 'qty' ? value : updated[index].qty) || 0;
        const rate = parseFloat(field === 'rate' ? value : updated[index].rate) || 0;
        updated[index].total = Math.round(qty * rate * 100) / 100;
      }
      return updated;
    });
  };

  const handleRemoveCustomItem = (index) => {
    setCustomItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleStartCreate = async () => {
    const num = await getNextInvoiceNumber();
    setInvoiceNumber(num);
    setSelectedDevices(new Set());
    setCustomItems([]);
    setInvoiceNotes('');
    setView('create');
  };

  const handleSubmitInvoice = async () => {
    if (allLineItems.length === 0) { showToast('Add at least one line item', 'error'); return; }
    if (!clientEmail) { showToast('Enter email address', 'error'); return; }

    setSubmitting(true);
    try {
      const deviceIds = [...new Set(deviceLineItems.map(li => li.device_id).filter(Boolean))];

      const invoiceId = await createPaymentInvoice({
        project_id: projectId,
        invoice_number: invoiceNumber,
        invoice_type: invoiceType,
        client: clientConfig.companyName,
        segment_ids: null,
        device_ids: JSON.stringify(deviceIds),
        line_items_json: JSON.stringify(allLineItems),
        subtotal,
        retainage_pct: 10,
        retainage_amount: retainage,
        net_amount: netAmount,
        status: 'Submitted',
        submitted_at: new Date().toISOString(),
        submitted_by: user?.email || 'admin',
        submitted_to_email: clientEmail,
        notes: invoiceNotes || null,
      });

      if (!invoiceId) { showToast('Failed to create invoice', 'error'); setSubmitting(false); return; }

      // Mark devices as billed
      if (deviceIds.length > 0) {
        await markDevicesBilled(deviceIds, 'Submitted', invoiceId, 'invoice');
      }

      // Send email
      const emailHtml = generateInvoiceEmailHtml(allLineItems, project, invoiceNumber, invoiceType, subtotal, retainage, netAmount, invoiceNotes);
      await sendEmailNotification(clientEmail, `Invoice ${invoiceNumber} — ${project?.name || projectId}`, emailHtml);

      await loadData();
      setView('list');
      showToast(`Invoice ${invoiceNumber} sent to ${clientEmail}`);
    } catch (err) {
      console.error('Submit invoice failed:', err);
      showToast('Failed: ' + err.message, 'error');
    }
    setSubmitting(false);
  };

  const handleLogApproval = async (invoiceId) => {
    setActionLoading(true);
    try {
      await updatePaymentInvoiceField(invoiceId, 'status', 'Approved');
      await updatePaymentInvoiceField(invoiceId, 'metronet_approved_at', new Date().toISOString());
      const inv = invoices.find(i => i.invoice_id === invoiceId);
      if (inv?.device_ids) {
        const devIds = JSON.parse(inv.device_ids);
        if (devIds.length) await markDevicesBilled(devIds, 'Approved', invoiceId, 'invoice');
      }
      await loadData();
      setApprovalModal(null);
      showToast('Approval logged');
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    setActionLoading(false);
  };

  const handleLogPayment = async (invoiceId) => {
    if (!paymentAmount) { showToast('Enter amount', 'error'); return; }
    setActionLoading(true);
    try {
      await updatePaymentInvoiceField(invoiceId, 'status', 'Paid');
      await updatePaymentInvoiceField(invoiceId, 'payment_received_at', new Date().toISOString());
      await updatePaymentInvoiceField(invoiceId, 'payment_amount', paymentAmount);
      if (paymentRef) await updatePaymentInvoiceField(invoiceId, 'payment_reference', paymentRef);
      const inv = invoices.find(i => i.invoice_id === invoiceId);
      if (inv?.device_ids) {
        const devIds = JSON.parse(inv.device_ids);
        if (devIds.length) await markDevicesBilled(devIds, 'Paid', invoiceId, 'invoice');
      }
      await loadData();
      setPaymentModal(null);
      setPaymentAmount('');
      setPaymentRef('');
      showToast('Payment recorded');
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    setActionLoading(false);
  };

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, backgroundColor: toast.type === 'error' ? '#dc3545' : '#4CAF50', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => view === 'create' ? setView('list') : setCurrentPage('admin-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}><ArrowLeft size={20} /></button>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{view === 'create' ? 'Create Invoice' : `${clientName} Invoices`}</h1>
            <div style={{ fontSize: 13, color: muted }}>{project?.name || 'All Projects'} — Splicing, Special Builds & Maintenance</div>
          </div>
        </div>
        {view === 'list' && (
          <button onClick={handleStartCreate} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', backgroundColor: accent, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            <Plus size={16} /> New Invoice
          </button>
        )}
      </div>

      <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
        {/* ====== LIST VIEW ====== */}
        {view === 'list' && (
          <>
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: muted }}>
                <FileText size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No Invoices Yet</div>
                <div style={{ fontSize: 13 }}>Create your first invoice for splicing or special work.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invoices.map(inv => {
                  const isExpanded = expandedInvoice === inv.invoice_id;
                  const items = inv.line_items_json ? JSON.parse(inv.line_items_json) : [];

                  return (
                    <div key={inv.invoice_id} style={{ backgroundColor: cardBg, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                      <div onClick={() => setExpandedInvoice(isExpanded ? null : inv.invoice_id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <BillingBadge status={inv.status} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number}</div>
                            <div style={{ fontSize: 12, color: muted }}>{inv.invoice_type} | {items.length} items</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(inv.subtotal)}</div>
                            {inv.status === 'Paid' && <div style={{ fontSize: 12, color: '#4CAF50' }}>Paid: {fmt(inv.payment_amount)}</div>}
                          </div>
                          {isExpanded ? <ChevronUp size={16} color={muted} /> : <ChevronDown size={16} color={muted} />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${border}` }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, padding: '12px 0' }}>
                            <InfoItem label="Submitted" value={inv.submitted_at ? new Date(inv.submitted_at).toLocaleString() : '—'} />
                            <InfoItem label="Sent To" value={inv.submitted_to_email || '—'} />
                            <InfoItem label="Retainage (10%)" value={fmt(inv.retainage_amount)} />
                            <InfoItem label="Net Amount" value={fmt(inv.net_amount)} />
                            <InfoItem label="Approved" value={inv.metronet_approved_at ? new Date(inv.metronet_approved_at).toLocaleString() : '—'} />
                            <InfoItem label="Payment Ref" value={inv.payment_reference || '—'} />
                          </div>

                          {items.length > 0 && (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                                  {['Code', 'Description', 'Qty', 'Rate', 'Total'].map(h => (
                                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${border}` }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((li, i) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{li.code}</td>
                                    <td style={{ padding: '8px 10px' }}>{li.description}</td>
                                    <td style={{ padding: '8px 10px' }}>{li.qty}</td>
                                    <td style={{ padding: '8px 10px' }}>${parseFloat(li.rate).toFixed(2)}</td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>${parseFloat(li.total).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            {inv.status === 'Submitted' && (
                              <button onClick={() => setApprovalModal(inv.invoice_id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <Check size={14} /> Log Approval
                              </button>
                            )}
                            {['Submitted', 'Approved'].includes(inv.status) && (
                              <button onClick={() => { setPaymentModal(inv.invoice_id); setPaymentAmount(String(inv.net_amount || inv.subtotal)); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <CreditCard size={14} /> Log Payment
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ====== CREATE VIEW ====== */}
        {view === 'create' && (
          <>
            {/* Invoice type + number */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Invoice Type</label>
                <select value={invoiceType} onChange={e => setInvoiceType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13 }}>
                  <option value="splicing">Splicing</option>
                  <option value="special_build">Special Build</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Invoice Number</label>
                <input type="text" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Send To</label>
                <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder={`billing@${clientName.toLowerCase().replace(/\s+/g, '')}.com`} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Device selection */}
            {billableDevices.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Select QC-Approved Devices ({billableDevices.length})</h3>
                  <button onClick={handleSelectAll} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {selectedDevices.size === billableDevices.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8 }}>
                  {billableDevices.map(dev => (
                    <label key={dev.device_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: `1px solid ${border}`, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedDevices.has(dev.device_id)} onChange={() => handleToggleDevice(dev.device_id)} />
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{dev.device_id}</span>
                        <span style={{ fontSize: 12, color: muted, marginLeft: 8 }}>{dev.device_type} {dev.splice_type || ''} — {dev.location || '—'}</span>
                      </div>
                      <span style={{ fontSize: 12, color: muted }}>{dev.unit_code}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Custom line items */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Custom Line Items</h3>
                <button onClick={handleAddCustomItem} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <Plus size={14} /> Add Item
                </button>
              </div>
              {customItems.map((item, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px 80px 100px 32px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input placeholder="Code" value={item.code} onChange={e => handleUpdateCustomItem(i, 'code', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 12 }} />
                  <input placeholder="Description" value={item.description} onChange={e => handleUpdateCustomItem(i, 'description', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 12 }} />
                  <input type="number" placeholder="Qty" value={item.qty} onChange={e => handleUpdateCustomItem(i, 'qty', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 12 }} />
                  <input placeholder="UOM" value={item.uom} onChange={e => handleUpdateCustomItem(i, 'uom', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 12 }} />
                  <input type="number" placeholder="Rate" value={item.rate} onChange={e => handleUpdateCustomItem(i, 'rate', e.target.value)} style={{ padding: '8px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 12 }} />
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{fmt(item.total)}</div>
                  <button onClick={() => handleRemoveCustomItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ backgroundColor: cardBg, borderRadius: 10, padding: 16, border: `1px solid ${border}`, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Subtotal ({allLineItems.length} items)</span>
                <span style={{ fontWeight: 700 }}>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: muted }}>
                <span>Retainage (10%)</span>
                <span>-{fmt(retainage)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${border}` }}>
                <span style={{ fontWeight: 700 }}>Net Amount</span>
                <span style={{ fontWeight: 700, fontSize: 18, color: '#4CAF50' }}>{fmt(netAmount)}</span>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Notes</label>
              <textarea value={invoiceNotes} onChange={e => setInvoiceNotes(e.target.value)} rows={2} placeholder="Optional notes..." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmitInvoice} disabled={submitting || allLineItems.length === 0} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
              padding: '14px 24px', backgroundColor: submitting ? muted : accent, color: '#fff',
              border: 'none', borderRadius: 10, cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 15,
            }}>
              {submitting ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
              {submitting ? 'Sending...' : `Submit Invoice ${invoiceNumber} to ${clientName}`}
            </button>
          </>
        )}
      </div>

      {/* Approval Modal */}
      {approvalModal && (
        <Modal onClose={() => setApprovalModal(null)} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Confirm Approval</h3>
          <p style={{ fontSize: 14, color: muted, margin: '0 0 20px' }}>Mark this invoice as approved by {clientName}?</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setApprovalModal(null)} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={() => handleLogApproval(approvalModal)} disabled={actionLoading} style={{ padding: '10px 20px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <Modal onClose={() => { setPaymentModal(null); setPaymentAmount(''); setPaymentRef(''); }} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Record Payment</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Amount *</label>
              <input type="number" step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Reference</label>
              <input type="text" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setPaymentModal(null); setPaymentAmount(''); setPaymentRef(''); }} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={() => handleLogPayment(paymentModal)} disabled={actionLoading || !paymentAmount} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </Modal>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function BillingBadge({ status }) {
  const colors = {
    'Draft': { bg: '#9E9E9E20', text: '#9E9E9E' },
    'Submitted': { bg: '#FFB80020', text: '#FFB800' },
    'Approved': { bg: '#2196F320', text: '#2196F3' },
    'Paid': { bg: '#4CAF5020', text: '#4CAF50' },
  };
  const c = colors[status] || colors['Draft'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, backgroundColor: c.bg, color: c.text, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: c.text }} />
      {status}
    </span>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#8892b0', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}

function Modal({ children, onClose, darkMode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: darkMode ? '#112240' : '#ffffff', borderRadius: 12, padding: 24, maxWidth: 520, width: '90%', position: 'relative', color: darkMode ? '#ffffff' : '#1e293b' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#8892b0' : '#64748b' }}><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

function generateInvoiceEmailHtml(lineItems, project, invoiceNumber, invoiceType, subtotal, retainage, netAmount, notes) {
  const rows = lineItems.map(li => `
    <tr>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;font-weight:600;">${li.code}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;">${li.description}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">${li.qty}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center;">${li.uom}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">$${parseFloat(li.rate).toFixed(2)}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-weight:600;">$${parseFloat(li.total).toFixed(2)}</td>
    </tr>
  `).join('');

  return `
    <p><strong>Invoice:</strong> ${invoiceNumber} | <strong>Type:</strong> ${invoiceType}</p>
    <p><strong>Project:</strong> ${project?.name || 'N/A'} | <strong>WO:</strong> ${project?.wo_number || 'N/A'}</p>
    <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
    ${notes ? `<p><strong>Notes:</strong> ${notes}</p>` : ''}

    <table style="width:100%;border-collapse:collapse;font-size:13px;margin:16px 0;">
      <thead>
        <tr style="background:#f0f4f8;">
          <th style="padding:10px;text-align:left;border-bottom:2px solid #ccc;">Code</th>
          <th style="padding:10px;text-align:left;border-bottom:2px solid #ccc;">Description</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Qty</th>
          <th style="padding:10px;text-align:center;border-bottom:2px solid #ccc;">UOM</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Rate</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>

    <table style="width:300px;margin-left:auto;font-size:14px;">
      <tr><td style="padding:4px 0;">Subtotal:</td><td style="text-align:right;font-weight:600;">$${subtotal.toFixed(2)}</td></tr>
      <tr><td style="padding:4px 0;color:#888;">Retainage (10%):</td><td style="text-align:right;color:#888;">-$${retainage.toFixed(2)}</td></tr>
      <tr style="border-top:2px solid #333;"><td style="padding:8px 0;font-weight:700;">Net Amount:</td><td style="text-align:right;font-weight:700;font-size:16px;color:#2e7d32;">$${netAmount.toFixed(2)}</td></tr>
    </table>

    <p style="margin:16px 0 0;font-size:12px;color:#999;">Terms: Net 30 | Please confirm receipt and approval.</p>
  `;
}

export default PaymentInvoicePage;
